// Tasks → Projects tab: every project of the org with owner, status (chosen by
// the owner, overdue tasks shown beside it), progress and target date.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Trash2 } from 'lucide-react'
import { DataTableShell } from '../../components/ui/data-table-shell'
import { EmptyState } from '../../components/ui/empty-state'
import { Button } from '../../components/ui/button'
import { toast } from '../../lib/toast'
import { useT } from '../../i18n'
import { staffTasksI18n } from '../../i18n/shared/staff-tasks'
import { deleteTemplate, listProjects, listTemplates, staffTaskKeys } from './api'
import { formatDay, projectRoute, type TaskShell } from './lib'
import { PrivateLock, ProgressBar, ProjectStatusBadge } from './ProjectBits'

export function ProjectsTab({
  shell,
  folderId = null,
  onCreate,
}: {
  shell: TaskShell
  /** One folder's projects, or `'none'` for those without one; null = all. */
  folderId?: string | null
  onCreate: () => void
}) {
  const t = useT(staffTasksI18n)
  const p = t.projects
  const queryClient = useQueryClient()
  const projects = useQuery({ queryKey: staffTaskKeys.projects, queryFn: listProjects })
  const templates = useQuery({ queryKey: staffTaskKeys.templates, queryFn: listTemplates })
  const removeTemplate = useMutation({
    mutationFn: deleteTemplate,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: staffTaskKeys.templates }),
    onError: () => toast.error(t.toasts.error),
  })
  const items = (projects.data?.items ?? []).filter((project) =>
    folderId === null
      ? true
      : folderId === 'none'
        ? !project.folder_id
        : project.folder_id === folderId,
  )

  if (projects.isError) {
    return (
      <p role="alert" className="text-sm text-[var(--color-danger)]">
        {t.loadError}
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {!projects.isLoading && items.length === 0 ? (
        <EmptyState
          icon="taskComplete"
          title={p.empty.title}
          description={p.empty.description}
          action={<Button onClick={onCreate}>{p.newProject}</Button>}
        />
      ) : (
        <DataTableShell aria-busy={projects.isLoading}>
          <table data-testid="staff-project-table" className="min-w-[44rem]">
            <thead>
              <tr>
                <th>{p.columns.project}</th>
                <th className="w-36">{p.columns.owner}</th>
                <th className="w-32">{p.columns.status}</th>
                <th className="w-32">{p.columns.progress}</th>
                <th className="w-28">{p.columns.target}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((project) => (
                <tr key={project.id} data-testid={`staff-project-row-${project.id}`}>
                  <td className="py-2">
                    <span className="inline-flex items-center gap-1.5">
                      <Link
                        {...projectRoute(shell, project.id)}
                        className="font-medium text-[var(--color-text)] hover:underline"
                      >
                        {project.name}
                      </Link>
                      {project.is_private && (
                        <PrivateLock memberCount={project.members?.length} />
                      )}
                    </span>
                    {project.current_stage && (
                      <div className="mt-0.5 text-xs text-[var(--color-text-muted)]">
                        {p.stageLabel(project.current_stage)}
                      </div>
                    )}
                  </td>
                  <td className="text-sm">
                    {project.owner?.display_name ?? (
                      <span className="text-[var(--color-text-muted)]">{p.noOwner}</span>
                    )}
                  </td>
                  <td>
                    <ProjectStatusBadge status={project.status} />
                    {project.tasks_overdue > 0 && project.status !== 'done' && (
                      <div className="mt-1 text-xs font-medium text-[var(--color-danger)]">
                        {p.overdueTasks(project.tasks_overdue)}
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="flex items-center gap-2 text-xs tabular-nums text-[var(--color-text-muted)]">
                      <ProgressBar
                        className="w-16"
                        done={project.tasks_done}
                        total={project.tasks_total}
                        label={p.tasksDone(project.tasks_done, project.tasks_total)}
                      />
                      {p.progress(project.tasks_done, project.tasks_total)}
                    </div>
                  </td>
                  <td className="text-sm">
                    {formatDay(project.target_date) ?? (
                      <span className="text-[var(--color-text-muted)]">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DataTableShell>
      )}

      {(templates.data?.items.length ?? 0) > 0 && (
        <section aria-labelledby="staff-project-templates" className="text-sm">
          <h2 id="staff-project-templates" className="m-0 mb-2 text-sm font-semibold">
            {p.templates.title}
          </h2>
          <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
            {(templates.data?.items ?? []).map((template) => (
              <li
                key={template.id}
                className="inline-flex items-center gap-1 rounded-full border border-[var(--color-border)] bg-[var(--color-surface-muted)] py-0.5 pl-3 pr-1 text-xs"
              >
                {template.name}
                <span className="text-[var(--color-text-muted)]">
                  · {p.templates.size(template.stage_count, template.task_count)}
                </span>
                <button
                  type="button"
                  aria-label={p.templates.remove(template.name)}
                  onClick={() => removeTemplate.mutate(template.id)}
                  className="inline-flex h-5 w-5 items-center justify-center rounded-full text-[var(--color-text-muted)] hover:text-[var(--color-danger)]"
                >
                  <Trash2 className="h-3 w-3" aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}
